import { useEffect, useState } from 'react';
import { collection, doc, getDoc, getDocs, limit, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { isNonProdEnvironment } from '../utils/environmentTheme';

/**
 * Read organisations/{id}.llmFallbackConfig.enabled.
 *
 * Fail-closed: any missing doc, missing field, or read error returns false
 * so the compare UI cannot appear unless the org has been switched on
 * deliberately. Mirrors the backend policy resolver.
 *
 * @param {Object} params
 * @param {string} [params.orgId] - organisations/{orgId} document id
 * @param {string} [params.organisationName] - organisations.name (token claim)
 * @returns {Promise<boolean>}
 */
export async function fetchOrgLlmFallbackEnabled({ orgId, organisationName } = {}) {
  try {
    if (orgId) {
      const snap = await getDoc(doc(db, 'organisations', orgId));
      if (snap.exists()) {
        return Boolean(snap.data()?.llmFallbackConfig?.enabled);
      }
    }

    // Token orgId can be missing on older clinicians; the org name claim is
    // always present for providers and is an allowed Firestore read path.
    if (organisationName) {
      const snapshot = await getDocs(
        query(
          collection(db, 'organisations'),
          where('name', '==', organisationName),
          limit(1)
        )
      );
      if (!snapshot.empty) {
        return Boolean(snapshot.docs[0].data()?.llmFallbackConfig?.enabled);
      }
    }
  } catch (err) {
    console.error('[useOrgLlmFallbackEnabled] Org fallback lookup failed:', err);
  }

  return false;
}

/**
 * Whether this clinician's org has LLM fallback enabled, and only in
 * dev/staging. Production always returns false (the compare button must
 * never render there even if an org doc were flipped on).
 *
 * Starts false and flips true only after a successful org-doc read, so
 * other staging orgs never see a flash of the compare strip.
 *
 * @param {Object} [params]
 * @param {string} [params.orgId]
 * @param {string} [params.organisationName]
 * @returns {boolean}
 */
export function useOrgLlmFallbackEnabled({ orgId, organisationName } = {}) {
  const isNonProd = isNonProdEnvironment();
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (!isNonProd) {
      setEnabled(false);
      return undefined;
    }

    let cancelled = false;

    fetchOrgLlmFallbackEnabled({ orgId, organisationName }).then((value) => {
      if (!cancelled) setEnabled(value);
    });

    return () => {
      cancelled = true;
    };
  }, [isNonProd, orgId, organisationName]);

  return isNonProd && enabled;
}
